import { type TruthState } from "./Truth";

interface NumericQueryTruthInput {
  /** The numerator's query is still in flight. */
  loading: boolean;
  /** The numerator's query failed. */
  failed: boolean;
  /** Human name of the system the NUMERATOR came from. */
  source: string;
  value: unknown;
  of: unknown;
  /**
   * PKT-0441 F3 · the denominator often comes from a DIFFERENT query than the
   * numerator (the fix-queue count is divided by box-vitals' active_properties).
   * Without its own provenance the tile blames `source` — i.e. it names the
   * system that WORKED and stays silent about the one that failed, sending the
   * operator to debug the wrong service. Pass these whenever `of` is not
   * produced by `source`.
   */
  ofSource?: string;
  ofLoading?: boolean;
  ofFailed?: boolean;
  asOf: string;
}

/**
 * A real number is finite. `typeof NaN === "number"` and
 * `typeof Infinity === "number"`, so `typeof` alone lets a nonsense value
 * render as a confident `ok` ("NaN of 202 (NaN%)"). `Number.isFinite` is the
 * check that actually asks the question the guard exists to ask.
 */
function isRealNumber(candidate: unknown): candidate is number {
  return typeof candidate === "number" && Number.isFinite(candidate);
}

export function numericQueryTruth({
  loading,
  failed,
  source,
  value,
  of,
  ofSource,
  ofLoading,
  ofFailed,
  asOf,
}: NumericQueryTruthInput): TruthState<number> {
  // When no separate denominator source is declared, the denominator is
  // produced by the same query as the numerator.
  const denominatorSource = ofSource === undefined ? source : ofSource;

  if (loading || ofLoading) return { status: "loading" };
  if (failed) {
    return { status: "unknown", source, reason: "the read failed" };
  }
  if (ofFailed) {
    return {
      status: "unknown",
      source: denominatorSource,
      reason: "the denominator's read failed",
    };
  }
  if (!isRealNumber(value)) {
    return {
      status: "unknown",
      source,
      reason:
        typeof value === "number"
          ? "the count was not a real number"
          : "the count was absent",
    };
  }
  if (!isRealNumber(of)) {
    return {
      status: "unknown",
      source: denominatorSource,
      reason:
        typeof of === "number"
          ? "the denominator was not a real number"
          : "the denominator was absent",
    };
  }
  return { status: "ok", value, of, as_of: asOf };
}
